import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert, { type AlertVariant } from "components/ui/Alert/Alert";
import DataTable, { type DataTableColumn } from "components/ui/DataTable/DataTable";
import PaginationFooter from "components/ui/PaginationFooter/PaginationFooter";
import Icon from "components/ui/Icon/Icon";
import { useI18n } from "context/I18nContext";
import { fetchAccounts, type PaginationMeta } from "utils/accountsApi";
import {
  formatDate,
  toDateInputValue,
  toRangeEndIso,
  toRangeStartIso,
} from "utils/dateUtils";
import styles from "./AccountsPage.module.scss";

type Account = {
  id: string;
  accountNo: string;
  customerName: string;
  walletBalance: number;
  status: string;
  createdDate: string;
  updatedDate: string;
};

const PAGE_SIZE = 10;
const DATE_FROM_PARAM = "fromDate";
const DATE_TO_PARAM = "toDate";

type FlashMessage = {
  variant: AlertVariant;
  message: string;
};

const formatCurrency = (value: unknown): string => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(numeric)) {
    return `৳ ${numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return "৳ 0.00";
};

function AccountsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const location = useLocation() as ReturnType<typeof useLocation> & {
    state?: { flash?: FlashMessage };
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Math.max(1, Number(searchParams.get("page")) || 1);
  const rawSearchParam = searchParams.get("search") ?? "";
  const searchTerm = rawSearchParam.trim();
  const fromDateParam = searchParams.get(DATE_FROM_PARAM) ?? "";
  const toDateParam = searchParams.get(DATE_TO_PARAM) ?? "";
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [searchInput, setSearchInput] = useState(rawSearchParam);
  const [fromDateInput, setFromDateInput] = useState(() => toDateInputValue(fromDateParam));
  const [toDateInput, setToDateInput] = useState(() => toDateInputValue(toDateParam));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<FlashMessage | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchLocked, setSearchLocked] = useState(false);
  const searchCooldownRef = useRef<number | null>(null);

  const columns = useMemo<DataTableColumn<Account>[]>(
    () => [
      {
        id: "accountNo",
        header: t("accountsPage.table.accountNo"),
        accessorKey: "accountNo",
      },
      {
        id: "customerName",
        header: t("accountsPage.table.customerName"),
        accessorKey: "customerName",
      },
      {
        id: "walletBalance",
        header: t("accountsPage.table.walletBalance"),
        accessorKey: "walletBalance",
        cell: (value) => <span>{formatCurrency(value)}</span>,
        align: "right",
      },
      {
        id: "status",
        header: t("accountsPage.table.status"),
        accessorKey: "status",
        cell: (value) => {
          const text = typeof value === "string" && value.length
            ? value
            : t("accountsPage.unknownStatus");
          return <span>{text}</span>;
        },
        align: "center",
      },
      {
        id: "createdDate",
        header: t("accountsPage.table.created"),
        accessorKey: "createdDate",
        cell: (value) => <span>{formatDate(value)}</span>,
      },
      {
        id: "updatedDate",
        header: t("accountsPage.table.updated"),
        accessorKey: "updatedDate",
        cell: (value) => <span>{formatDate(value)}</span>,
      },
      {
        id: "action",
        header: t("accountsPage.table.action"),
        cell: (_, row) => (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() =>
              navigate(`/accounts/${row.id}/edit`, {
                state: {
                  from: { pathname: "/accounts", search: location.search },
                },
              })
            }
          >
            {t("accountsPage.table.details")}
          </button>
        ),
        align: "center",
      },
    ],
    [navigate, location.search, t]
  );

  useEffect(() => {
    if (location.state?.flash) {
      setFlash(location.state.flash);
      window.scrollTo({ top: 0, behavior: "smooth" });
      navigate(".", { replace: true, state: null });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!flash) {
      return;
    }
    const timer = window.setTimeout(() => setFlash(null), 5000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  useEffect(() => {
    setSearchInput(rawSearchParam);
  }, [rawSearchParam]);

  useEffect(() => {
    setFromDateInput(toDateInputValue(fromDateParam));
  }, [fromDateParam]);

  useEffect(() => {
    setToDateInput(toDateInputValue(toDateParam));
  }, [toDateParam]);

  useEffect(
    () => () => {
      if (searchCooldownRef.current) {
        window.clearTimeout(searchCooldownRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchAccounts({
          page: pageParam,
          pageSize: PAGE_SIZE,
          search: searchTerm || undefined,
          signal: controller.signal,
          fromDate: fromDateParam || undefined,
          toDate: toDateParam || undefined,
        });
        const mappedAccounts = response.items.map((item) => ({
          id: String(item.id),
          accountNo: item.accountNo,
          customerName: item.customerName ?? "—",
          walletBalance: Number(item.walletBalance ?? 0),
          status:
            item.statusName ??
            (item.statusId !== undefined && item.statusId !== null
              ? String(item.statusId)
              : "Unknown"),
          createdDate: item.createdAt ?? "",
          updatedDate: item.updatedAt ?? "",
        }));
        setAccounts(mappedAccounts);
        setMeta(response.meta);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        setError((err as Error).message || t("accountsPage.error"));
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => controller.abort();
  }, [pageParam, searchTerm, fromDateParam, toDateParam, t, reloadKey]);

  const totalPages = meta?.pages ?? (accounts.length > 0 ? 1 : 0);
  const currentPage = meta?.page ?? pageParam;

  const handlePageChange = (nextPage: number) => {
    if (nextPage === currentPage) {
      return;
    }
    const safeMax = Math.max(totalPages, 1);
    if (nextPage < 1 || nextPage > safeMax) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next, { replace: false });
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchLocked) {
      return;
    }
    setSearchLocked(true);
    if (searchCooldownRef.current) {
      window.clearTimeout(searchCooldownRef.current);
    }
    searchCooldownRef.current = window.setTimeout(() => setSearchLocked(false), 3000);

    const trimmed = searchInput.trim();
    const nextFromParam = toRangeStartIso(fromDateInput);
    const nextToParam = toRangeEndIso(toDateInput);
    if (
      trimmed === searchTerm &&
      (nextFromParam ?? "") === fromDateParam &&
      (nextToParam ?? "") === toDateParam
    ) {
      setReloadKey((prev) => prev + 1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (trimmed) {
      next.set("search", trimmed);
    } else {
      next.delete("search");
    }
    if (nextFromParam) {
      next.set(DATE_FROM_PARAM, nextFromParam);
    } else {
      next.delete(DATE_FROM_PARAM);
    }
    if (nextToParam) {
      next.set(DATE_TO_PARAM, nextToParam);
    } else {
      next.delete(DATE_TO_PARAM);
    }
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>{t("accountsPage.title")}</h1>
            <p className={styles.subtitle}>{t("accountsPage.subtitle")}</p>
          </div>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() =>
              navigate("/accounts/new", {
                state: {
                  from: { pathname: "/accounts", search: location.search },
                },
              })
            }
          >
            <Icon name="add" color="inherit" />
            {t("accountsPage.add")}
          </button>
        </div>

        <form className={styles.searchBar} onSubmit={handleSearchSubmit}>
          <label className={styles.fieldGroup} htmlFor="accounts-search">
            <span className={styles.fieldLabel}>{t("accountsPage.searchLabel")}</span>
            <input
              id="accounts-search"
              className={styles.searchInput}
              type="search"
              placeholder={t("accountsPage.searchPlaceholder")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="accounts-from-date">
            <span className={styles.fieldLabel}>{t("accountsPage.filters.from")}</span>
            <input
              id="accounts-from-date"
              className={styles.dateInput}
              type="date"
              value={fromDateInput}
              max={toDateInput || undefined}
              onChange={(event) => setFromDateInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="accounts-to-date">
            <span className={styles.fieldLabel}>{t("accountsPage.filters.to")}</span>
            <input
              id="accounts-to-date"
              className={styles.dateInput}
              type="date"
              value={toDateInput}
              min={fromDateInput || undefined}
              onChange={(event) => setToDateInput(event.target.value)}
            />
          </label>
          <button type="submit" className={styles.searchButton} disabled={searchLocked}>
            {t("datatable.search")}
          </button>
        </form>

        {(flash || error) && (
          <div className={styles.alertStack}>
            {flash && (
              <Alert
                variant={flash.variant}
                dismissible
                onClose={() => setFlash(null)}
              >
                {flash.message}
              </Alert>
            )}
            {error && (
              <Alert
                variant="error"
                dismissible
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          data={accounts}
          globalSearch={false}
          loading={loading}
          loadingText={t("accountsPage.loading")}
          emptyState={
            loading
              ? t("accountsPage.loading")
              : error
                ? t("accountsPage.error")
                : t("accountsPage.empty")
          }
          manualPagination={
            meta
              ? {
                  pageIndex: Math.max((meta.page ?? 1) - 1, 0),
                  pageCount: Math.max(meta.pages ?? 1, 1),
                  pageSize: meta.pageSize ?? PAGE_SIZE,
                  totalItems: meta.total ?? accounts.length,
                  onPageChange: (nextIndex) => handlePageChange(nextIndex + 1),
                }
              : undefined
          }
          showPaginationInfo={false}
          showPaginationControls={false}
        />

        {meta && (
          <PaginationFooter
            totalLabel={t("accountsPage.meta.total")}
            pageLabel={t("accountsPage.meta.page")}
            pageSizeLabel={t("accountsPage.meta.pageSize")}
            total={meta.total ?? 0}
            page={meta.page ?? 1}
            pageCount={Math.max(meta.pages ?? 1, 1)}
            pageSize={meta.pageSize ?? PAGE_SIZE}
            previousLabel={t("datatable.previous")}
            nextLabel={t("datatable.next")}
            goToLabel={t("accountsPage.pagination.goToLabel")}
            goButtonLabel={t("accountsPage.pagination.goButton")}
            invalidMessage={(max) => t("accountsPage.pagination.invalidPage", { max })}
            onPageChange={handlePageChange}
          />
        )}
      </Card>
    </div>
  );
}

export default AccountsPage;
